// [src/pages/MyPage/MyPage.jsx]

import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { getTokenStatus, clearAllAuthStorage } from 'utils/tokenManager';
import { API_BASE_URL } from 'config/apiConfig';

// CSS imports
import 'styles/Font.css';

// Image imports
import profileImage from "assets/images/icon/profile-image-icon.svg";
import arrowRight from "assets/images/icon/arrow-right-icon.svg";
import crownIcon from "assets/images/icon/crown-icon.svg"

// Components imports
import Footer from "components/layout/Footer/Footer";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
import NotYetModal from "components/ui/Modals/NotYetModal/NotYetModal";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";


const formatPhoneNumber = (rawPhoneNumber) => {
    if (!rawPhoneNumber) return '';
    const cleaned = ('' + rawPhoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3,4})(\d{4})$/);
    if (match) {
        return match[1] + '-' + match[2] + '-' + match[3];
    }
    return rawPhoneNumber;
};

function MyPage() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();
    const navigate = useNavigate();

    const [userInfo, setUserInfo] = useState(null);
    // const [paymentStatus, setPaymentStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [profileImgSrc, setProfileImgSrc] = useState(profileImage);
    const [subscriptionName, setSubscriptionName] = useState('일반권');

    const [isNotYetModalOpen, setIsNotYetModalOpen] = useState(false);
    const [notYetMessage, setNotYetMessage] = useState("");

    const handleCloseNotYetModal = () => {
        setIsNotYetModalOpen(false);
        setNotYetMessage("");
    };

    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

    const handleLogoutClick = () => {
        setIsLogoutModalOpen(true);
    };

    const handleConfirmLogout = () => {
        setIsLogoutModalOpen(false);
        clearAllAuthStorage();
        navigate('/');
    };

    const handleCancelLogout = () => {
        setIsLogoutModalOpen(false);
    };

    const handleMyReviewClick = () => {
        navigate('/my/reviews');
    };

    const handleAttendanceClick = () => {
        navigate('/my/attendance');
    };
    const handleProfileClick = () => {
        navigate('/my/profile');
    };

    // --- 데이터 로드 ---
    useEffect(() => {
        const fetchMyData = async () => {
            const { token, isExpired } = getTokenStatus();

            if (!token || isExpired) {
                clearAllAuthStorage();
                setIsLoading(false);
                navigate('/', { replace: true });
                return;
            }

            const storedInfoStr = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
            // const storedPayment = sessionStorage.getItem('paymentStatus');

            if (storedInfoStr) {
                try {
                    const parsedInfo = JSON.parse(storedInfoStr);
                    setUserInfo(parsedInfo);
                    // setPaymentStatus(storedPayment);
                    if (parsedInfo.userImg) updateProfileImage(parsedInfo.userImg);
                } catch (e) {
                    console.error(e);
                }
            }

            try {
                const response = await fetch(`${API_BASE_URL}/userInfo`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    const data = await response.json();
                    const latestInfo = data.data?.userInfo || {};

                    setUserInfo(latestInfo);
                    updateProfileImage(latestInfo.userImg || latestInfo.img || latestInfo.profileImage);
                    sessionStorage.setItem('userInfo', JSON.stringify(latestInfo));
                }
            } catch (error) {
                console.error("MyPage info fetch error:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMyData();
    }, [navigate]);

    // 구독 정보 조회
    useEffect(() => {
        const fetchSubscriptionInfo = async () => {
            const { token } = getTokenStatus();
            if (!token) return;

            try {
                const resL = await fetch(`${API_BASE_URL}/oneOrder`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    },
                    body: new URLSearchParams({ pdKind: 'L' })
                });
                const dataL = await resL.json();
                const infoL = dataL.userInfo || dataL.data?.userInfo;

                if (infoL && infoL.odKey) {
                    setSubscriptionName('BOOST 구독권');
                    return;
                }

                const resC = await fetch(`${API_BASE_URL}/oneOrder`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    },
                    body: new URLSearchParams({ pdKind: 'C' })
                });
                const dataC = await resC.json();
                const infoC = dataC.userInfo || dataC.data?.userInfo;

                if (infoC && infoC.odKey) {
                    setSubscriptionName('PT 이용권');
                    return;
                }

                setSubscriptionName('일반권');

            } catch (e) {
                console.error("Subscription Fetch Error:", e);
                setSubscriptionName('일반권');
            }
        };

        fetchSubscriptionInfo();
    }, []);

    const updateProfileImage = (serverPath) => {
        if (!serverPath || (typeof serverPath === 'object' && Object.keys(serverPath).length === 0)) {
            setProfileImgSrc(profileImage);
            return;
        }

        if (typeof serverPath === 'string' && serverPath.trim() !== '') {
            if (serverPath.startsWith('http')) {
                setProfileImgSrc(serverPath);
            } else {
                const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
                const path = serverPath.startsWith('/') ? serverPath : `/${serverPath}`;
                setProfileImgSrc(`${baseUrl}${path}`);
            }
        } else {
            setProfileImgSrc(profileImage);
        }
    };

    if (isLoading) {
        return (
            <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                height: '100dvh', background: '#0D042D', color: 'white'
            }}>
                <p>내 정보 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>

            <main style={staticStyles.mainContent}>
                <div>
                    <img className="profileImage" src={profileImgSrc} alt="프로필 이미지" style={staticStyles.profileImage} />
                </div>

                <div className="userInfo" style={staticStyles.userInfo}>
                    <div className="userNameDiv" style={staticStyles.userNameDiv} typeof='button'
                        onClick={handleProfileClick}>
                        <span className="userName" style={staticStyles.userName}>
                            {userInfo?.userName || '사용자 이름'}
                        </span>
                        <div className="editDiv" style={staticStyles.editDiv} onClick={handleProfileClick}>
                            <img className="edit" src={arrowRight} alt="수정" style={staticStyles.editIcon} />
                        </div>
                    </div>
                    <span className="userId" style={staticStyles.userId}>
                        {formatPhoneNumber(userInfo?.phoneNumber || userInfo?.hp || userInfo?.phone || userInfo?.userId) || '-    -'}
                    </span>
                </div>

                {/* 이용중인 상품 카드 사용 미정 코드 유지 */}
                {/*<button className="Subscription" style={staticStyles.subscriptionButton}>
                    <div className="crownDiv" style={staticStyles.crownDiv}>
                        <img className="crownIcon" src={crownIcon} alt="crown" style={staticStyles.crownIcon}/>
                        <span className="subscriptionText" style={staticStyles.subscriptionText}>
                            {subscriptionName} 이용중
                        </span>
                    </div>
                </button>*/}

                <div className="myMenuDiv" style={staticStyles.myMenuDiv}>
                    <div className="usageInfo" style={staticStyles.menuSection}>
                        <p className="usageInfoTitle" style={staticStyles.menuTitle}>이용 정보</p>
                        <div className="usageInfoContent" style={staticStyles.menuContentBox}>
                            <button className="attendanceHistory" style={staticStyles.menuButton}
                                onClick={handleAttendanceClick}>
                                <span style={staticStyles.menuButtonText}>출석 내역</span>
                                <img className="arrowRight" src={arrowRight} alt="arrow"
                                    style={staticStyles.arrowRightIcon} />
                            </button>

                            <div className="horizonBar" style={staticStyles.horizonBar} />

                            <button className="myReview" style={staticStyles.menuButton}
                                onClick={handleMyReviewClick}>
                                <span style={staticStyles.menuButtonText}>내 리뷰</span>
                                <img className="arrowRight" src={arrowRight} alt="arrow"
                                    style={staticStyles.arrowRightIcon} />
                            </button>
                        </div>
                    </div>

                    <div className="paymentDiv" style={staticStyles.menuSection}>
                        <p className="paymentTitle" style={staticStyles.menuTitle}>결제</p>
                        <div className="paymentContent" style={staticStyles.menuContentBox}>
                            <button className="paymentHistory" style={staticStyles.menuButton}
                                onClick={() => navigate('/payment')}>
                                <span style={staticStyles.menuButtonText}>결제 내역</span>
                                <img className="paymentArrowRight" src={arrowRight} alt="arrow"
                                    style={staticStyles.arrowRightIcon} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="accountDiv" style={staticStyles.menuSection}>
                    <p className="accountTitle" style={staticStyles.menuTitle}>계정</p>
                    <div className="accountContent" style={staticStyles.menuContentBox}>
                        <button className="logoutButton" style={staticStyles.menuButton}
                            onClick={handleLogoutClick}>
                            <span style={staticStyles.menuButtonText}>로그아웃</span>
                        </button>
                    </div>
                </div>

                <Footer />
            </main>

            <ConfirmModal isOpen={isLogoutModalOpen} onClose={handleCancelLogout} onConfirm={handleConfirmLogout}
                title="로그아웃" message="정말로 로그아웃 하시겠습니까?" />
            <NotYetModal isOpen={isNotYetModalOpen} onClose={handleCloseNotYetModal} message={notYetMessage} />

        </div>
    );
}

const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D',
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    mainContent: {
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '19px',
        padding: '0 24px 100px 24px',
    },
    horizonBar: { width: '100%', height: '1px', background: '#3A394A', },
    profileImage: {
        width: '54px',
        height: '54px',
        marginTop: '10px',
        borderRadius: '50%',
        objectFit: 'cover',
    },
    userInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        color: '#9A9A9A',
        marginTop: '-13px',
    },
    userNameDiv: { display: 'flex', alignItems: 'center', gap: '4px', },
    userName: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '22px', color: '#FFFFFF', lineHeight: '17px', },
    editDiv: { width: '24px', height: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', },
    editIcon: { width: '7px', height: '14px', border: 'none', color: '#F5F8F9', },
    userId: { fontFamily: 'Pretendard', fontWeight: 300, fontSize: '14px', color: '#9A9A9A', },
    subscriptionButton: {
        width: '100%',
        height: '56px',
        borderRadius: '8px',
        background: 'linear-gradient(90deg, #8052FF 10%, #0231FB 90%)',
        border: 'none',
        padding: '16px 22px',
        display: 'flex',
        justifyContent: 'center', // 중앙 정렬 (기존: space-between)
        alignItems: 'center',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    crownDiv: { display: 'flex', alignItems: 'center', gap: '4px', },
    crownIcon: { width: '20px', height: '18px', color: '#FCFCFA', },
    subscriptionText: {
        fontFamily: 'Pretendard', fontWeight: 600, fontSize: '16px', color: '#FFFFFF', lineHeight: '21px',
    },
    arrowRightIcon: { width: '7px', height: '14px', border: 'none', color: '#F5F8F9' },
    myMenuDiv: { width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' },
    menuSection: { width: '100%', color: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: '12px' },
    menuTitle: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '20px', color: '#FFFFFF', margin: 0 },
    menuContentBox: {
        width: '100%',
        background: '#1B1931',
        borderRadius: '10px',
        paddingLeft: '22px',
        paddingRight: '22px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
    },
    menuButton: {
        width: '100%',
        height: '57px',
        background: "transparent",
        border: 'none',
        textAlign: 'left',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 0,
        cursor: 'pointer',
    },
    menuButtonText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '16px', color: '#FFFFFF', textAlign: 'left' },
    bottomNavWrapper: { flexShrink: 0 },
};

export default MyPage;/**/